import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { gql } from "@apollo/client";
import type { SyncablePriceListItemExternalReference } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { PriceListItemExternalReference as RxPriceListItemExternalReference } from "../collections/price-list-item-external-reference";
import type { RxDBCollections } from "../provider";
import {
  SyncPriceListItemExternalReferencePullDocument,
  type WholeSyncablePriceListItemExternalReferenceFragment,
} from "./__generated__/price-list-item-external-reference.gql";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicatePriceListItemExternalReference = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxPriceListItemExternalReference>;
}) => {
  const [enabled] = useFeatureFlag<boolean>(featureFlags.billing.priceListRxdb);

  const pull = useMemo<
    | ReplicationPullConfig<WholeSyncablePriceListItemExternalReferenceFragment>
    | undefined
  >(() => {
    if (!enabled) return undefined;
    return {
      query: SyncPriceListItemExternalReferencePullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { minutes: 5 },
    };
  }, [enabled]);

  return useGqlReplication<
    RxPriceListItemExternalReference,
    SyncablePriceListItemExternalReference,
    null
  >({
    db,
    collection,
    pull,
    mapGqlToRx:
      mapGqlPriceListItemExternalReferenceToRxPriceListItemExternalReference,
    batchSize: 100,
    metadataLocalStorageKeySuffix: "price-list-item-external-reference",
  });
};

export const mapGqlPriceListItemExternalReferenceToRxPriceListItemExternalReference =
  (
    externalReference: WholeSyncablePriceListItemExternalReferenceFragment,
  ): WithDeleted<RxPriceListItemExternalReference> => ({
    id: externalReference.id,
    itemId: externalReference.itemId,
    origin: externalReference.origin,
    foreignId: externalReference.foreignId,
    meta: {
      createdAt: externalReference.meta.createdAt,
      createdByUserId: externalReference.meta.createdByUserId,
      updatedAt: externalReference.meta.updatedAt,
      updatedByUserId: externalReference.meta.updatedByUserId,
    },
    _deleted: externalReference.meta.deleted,
  });

gql`
  fragment WholeSyncablePriceListItemExternalReference on SyncablePriceListItemExternalReference {
    id
    itemId
    origin
    foreignId
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncPriceListItemExternalReferencePull(
    $input: PullPriceListItemExternalReferenceInput!
  ) {
    pullPriceListItemExternalReference(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncablePriceListItemExternalReference
      }
    }
  }
`;
